"use client";

import { zodResolver } from "@hookform/resolvers/zod";
import { AnimatePresence, motion } from "framer-motion";
import {
  AlertCircle,
  Check,
  File,
  FileText,
  Loader2,
  Search,
  Upload,
} from "lucide-react";
import { useCallback, useEffect, useState } from "react";
import { useDropzone } from "react-dropzone";
import { useForm } from "react-hook-form";

import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Progress } from "@/components/ui/progress";
import { ScrollArea } from "@/components/ui/scroll-area";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";

import {
  DOCUMENT_CATEGORIES,
  DOCUMENT_TYPES,
  getDocumentTypesByCategory,
} from "@/lib/constants/documents";
import { cn } from "@/lib/utils";
import type { Document, DocumentCategory } from "@/schemas/document";
import { uploadSchema } from "@/schemas/document";
import type { z } from "zod";

const MAX_FILE_SIZE = 20 * 1024 * 1024; // 20MB
const ACCEPTED_FILE_TYPES = {
  "application/pdf": [".pdf"],
  "application/msword": [".doc"],
  "application/vnd.openxmlformats-officedocument.wordprocessingml.document": [
    ".docx",
  ],
  "image/jpeg": [".jpg", ".jpeg"],
  "image/png": [".png"],
  "image/heic": [".heic"],
  "image/heif": [".heif"],
  "application/vnd.ms-excel": [".xls"],
  "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet": [
    ".xlsx",
  ],
};

interface UploadingFile {
  file: File;
  progress: number;
  error?: string;
}

interface DocumentSelectorDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSelect: (document: Document | null) => void;
  onUpload: (data: z.infer<typeof uploadSchema>) => Promise<void>;
  title: string;
  description?: string;
  documents: Document[];
  selectedDocument?: Document | null;
  category: DocumentCategory;
  type: string;
  strictType?: boolean;
}

export function DocumentSelectorDialog({
  open,
  onOpenChange,
  onSelect,
  onUpload,
  title,
  description,
  documents,
  selectedDocument,
  category = "other",
  type,
  strictType = false,
}: DocumentSelectorDialogProps) {
  const [searchQuery, setSearchQuery] = useState("");
  const [uploadingFile, setUploadingFile] = useState<UploadingFile | null>(
    null,
  );
  const [selectedCategory, setSelectedCategory] = useState(category);
  const [selectedType, setSelectedType] = useState(type);

  const form = useForm<z.infer<typeof uploadSchema>>({
    resolver: zodResolver(uploadSchema),
    defaultValues: {
      file: undefined,
      category: category,
      type: type,
    },
  });

  // Modify this useEffect to only reset the form when dialog opens
  useEffect(() => {
    if (open) {
      form.reset({
        file: undefined,
        category,
        type,
      });
      setSelectedCategory(category);
      setSelectedType(type);
    }
  }, [open, category, type, form]);

  // Add a separate useEffect for category changes that doesn't run on initial mount
  useEffect(() => {
    if (!open) return; // Don't run if dialog is closed

    const validTypesForCategory = getDocumentTypesByCategory(selectedCategory);
    // Only update type if current type is invalid for the new category
    if (!validTypesForCategory.includes(form.getValues("type"))) {
      const newType = validTypesForCategory[0];
      form.setValue("type", newType);
      setSelectedType(newType);
    }
  }, [selectedCategory, form, open]);

  const onDrop = useCallback(
    (acceptedFiles: File[]) => {
      if (acceptedFiles[0]) {
        const file = acceptedFiles[0];
        form.setValue("file", file, { shouldValidate: true });
      }
    },
    [form],
  );

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: ACCEPTED_FILE_TYPES,
    maxSize: MAX_FILE_SIZE,
    multiple: false,
    disabled: !!uploadingFile,
  });

  const handleSubmit = async (data: z.infer<typeof uploadSchema>) => {
    setUploadingFile({ file: data.file, progress: 0 });

    // Simulate upload progress 😬
    const progressInterval = setInterval(() => {
      setUploadingFile((prev) =>
        prev ? { ...prev, progress: Math.min(prev.progress + 10, 90) } : null,
      );
    }, 100);

    try {
      await onUpload(data);
      setUploadingFile((prev) => (prev ? { ...prev, progress: 100 } : null));

      // Reset form and close dialog after short delay to show 100% progress
      setTimeout(() => {
        form.reset();
        onOpenChange(false);
      }, 500);
    } catch (error) {
      setUploadingFile((prev) =>
        prev
          ? {
              ...prev,
              error: error instanceof Error ? error.message : "Upload failed",
            }
          : null,
      );
    } finally {
      clearInterval(progressInterval);
    }
  };

  const filteredDocuments = documents.filter((doc) =>
    doc.name.toLowerCase().includes(searchQuery.toLowerCase()),
  );

  const handleCategoryChange = (value: string) => {
    const newCategory = value as DocumentCategory;
    form.setValue("category", newCategory);
    setSelectedCategory(newCategory);

    // Get valid types for new category
    const validTypes = getDocumentTypesByCategory(newCategory);
    // Reset to first valid type instead of empty string
    form.setValue("type", validTypes[0]);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[600px] overflow-hidden">
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
          {description && (
            <p className="text-sm text-muted-foreground">{description}</p>
          )}
        </DialogHeader>

        <Tabs defaultValue="existing">
          <TabsList className="grid w-full grid-cols-2">
            <TabsTrigger value="existing">Select Existing</TabsTrigger>
            <TabsTrigger value="upload">Upload New</TabsTrigger>
          </TabsList>

          <TabsContent value="existing" className="space-y-4">
            <div className="relative">
              <Search className="absolute left-3 top-2.5 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Search documents..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-9"
              />
            </div>

            <ScrollArea className="h-[300px] rounded-md border">
              <AnimatePresence mode="wait">
                {filteredDocuments.length > 0 ? (
                  <div className="p-2 space-y-2">
                    {filteredDocuments.map((doc) => (
                      <motion.div
                        key={doc.id}
                        initial={{ opacity: 0, y: 5 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: -5 }}
                      >
                        <Button
                          variant="ghost"
                          className="w-full justify-start gap-3 h-auto py-3"
                          onClick={() => onSelect(doc)}
                        >
                          <FileText className="h-4 w-4 text-muted-foreground flex-shrink-0" />
                          <div className="flex-1 text-left">
                            <p className="font-semibold text-sm">{doc.name}</p>
                            <p className="text-xs text-muted-foreground">
                              Uploaded{" "}
                              {new Date(doc.created_at).toLocaleDateString()}
                            </p>
                          </div>
                          {selectedDocument?.id === doc.id && (
                            <Check className="h-4 w-4 text-primary" />
                          )}
                        </Button>
                      </motion.div>
                    ))}
                  </div>
                ) : (
                  <div className="p-8 text-center">
                    <FileText className="h-8 w-8 text-muted-foreground mx-auto mb-3" />
                    <p className="text-sm font-semibold">No documents found</p>
                    <p className="text-xs text-muted-foreground mt-1">
                      Try adjusting your search or upload a new document
                    </p>
                  </div>
                )}
              </AnimatePresence>
            </ScrollArea>
          </TabsContent>

          <TabsContent value="upload" className="space-y-6">
            <Form {...form}>
              <form
                onSubmit={form.handleSubmit(handleSubmit)}
                className="space-y-6"
              >
                <FormField
                  control={form.control}
                  name="file"
                  render={({ field }) => (
                    <FormItem>
                      <FormControl>
                        <div
                          {...getRootProps()}
                          className={cn(
                            "border-2 border-dashed rounded-lg p-6 transition-colors duration-200",
                            "cursor-pointer hover:border-primary/50 hover:bg-muted/50",
                            isDragActive
                              ? "border-primary bg-muted/50"
                              : "border-muted",
                            uploadingFile && "pointer-events-none opacity-60",
                          )}
                        >
                          <input {...getInputProps()} />
                          <div className="flex flex-col items-center gap-3 text-center">
                            {field.value ? (
                              <div className="space-y-3 w-full">
                                <div className="flex items-center gap-3">
                                  {field.value.type.startsWith("image/") &&
                                  !field.value.type
                                    .toLowerCase()
                                    .includes("heic") ? (
                                    <div className="relative h-16 w-16 rounded-lg overflow-hidden flex-shrink-0">
                                      <img
                                        src={URL.createObjectURL(field.value)}
                                        alt="Preview"
                                        className="object-cover h-full w-full"
                                      />
                                    </div>
                                  ) : (
                                    <div className="h-16 w-16 rounded-lg bg-muted flex items-center justify-center flex-shrink-0">
                                      <File className="h-8 w-8 text-muted-foreground" />
                                    </div>
                                  )}
                                  <div className="flex-1 min-w-0">
                                    <div className="w-full overflow-hidden">
                                      <p className="text-sm font-semibold truncate max-w-[320px]">
                                        {field.value.name}
                                      </p>
                                      <p className="text-xs text-muted-foreground">
                                        {(
                                          field.value.size /
                                          1024 /
                                          1024
                                        ).toFixed(1)}{" "}
                                        MB
                                      </p>
                                    </div>
                                  </div>
                                </div>
                                <p className="text-xs text-muted-foreground">
                                  Click or drag to replace this file
                                </p>
                              </div>
                            ) : (
                              <>
                                <div className="h-12 w-12 rounded-full bg-muted flex items-center justify-center">
                                  <Upload className="h-6 w-6 text-muted-foreground" />
                                </div>
                                <div>
                                  <p className="text-sm font-semibold">
                                    {isDragActive ? (
                                      "Drop your file here"
                                    ) : (
                                      <>
                                        <span className="text-primary">
                                          Choose a file
                                        </span>{" "}
                                        or drag and drop
                                      </>
                                    )}
                                  </p>
                                  <p className="text-xs text-muted-foreground mt-1">
                                    PDF, DOC, Images, or Spreadsheets up to 20MB
                                  </p>
                                </div>
                              </>
                            )}
                          </div>
                        </div>
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <div className="grid grid-cols-2 gap-4">
                  <FormField
                    control={form.control}
                    name="category"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Category</FormLabel>
                        <Select
                          onValueChange={handleCategoryChange}
                          value={field.value}
                          disabled={strictType}
                        >
                          <FormControl>
                            <SelectTrigger>
                              <SelectValue placeholder="Select category" />
                            </SelectTrigger>
                          </FormControl>
                          <SelectContent>
                            {Object.entries(DOCUMENT_CATEGORIES).map(
                              ([key, label]) => (
                                <SelectItem key={key} value={key}>
                                  {label}
                                </SelectItem>
                              ),
                            )}
                          </SelectContent>
                        </Select>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="type"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Document Type</FormLabel>
                        {selectedCategory === "other" && !strictType ? (
                          <FormControl>
                            <Input
                              {...field}
                              placeholder="Enter document type"
                              maxLength={50}
                            />
                          </FormControl>
                        ) : (
                          <Select
                            onValueChange={field.onChange}
                            value={field.value}
                            disabled={strictType}
                          >
                            <FormControl>
                              <SelectTrigger>
                                <SelectValue placeholder="Select type" />
                              </SelectTrigger>
                            </FormControl>
                            <SelectContent>
                              {getDocumentTypesByCategory(selectedCategory).map(
                                (type) => (
                                  <SelectItem key={type} value={type}>
                                    {DOCUMENT_TYPES[
                                      type as keyof typeof DOCUMENT_TYPES
                                    ] ?? type}
                                  </SelectItem>
                                ),
                              )}
                            </SelectContent>
                          </Select>
                        )}
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </div>

                {uploadingFile && (
                  <div className="space-y-2">
                    <div className="flex items-center justify-between text-sm">
                      <span className="text-muted-foreground">
                        Uploading...
                      </span>
                      <span className="text-muted-foreground">
                        {Math.round(uploadingFile.progress)}%
                      </span>
                    </div>
                    <Progress value={uploadingFile.progress} className="h-1" />
                    {uploadingFile.error && (
                      <div className="flex items-center gap-1.5 text-xs text-destructive mt-1.5">
                        <AlertCircle className="h-3.5 w-3.5" />
                        {uploadingFile.error}
                      </div>
                    )}
                  </div>
                )}

                <Button
                  type="submit"
                  disabled={!form.formState.isValid || !!uploadingFile}
                  className="w-full"
                >
                  {uploadingFile ? (
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  ) : (
                    <Upload className="mr-2 h-4 w-4" />
                  )}
                  Upload Document
                </Button>
              </form>
            </Form>
          </TabsContent>
        </Tabs>
      </DialogContent>
    </Dialog>
  );
}
